import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const Image = styled.img`
  aspect-ratio: ${(props) => props.$aspect || '1'};
  background: #e2e8f0;
  border-radius: ${(props) => props.$radius || '0'};
  display: block;
  object-fit: cover;
  width: 100%;
`;

const Placeholder = styled.div`
  align-items: center;
  aspect-ratio: ${(props) => props.$aspect || '1'};
  background: #e2e8f0;
  border-radius: ${(props) => props.$radius || '0'};
  color: #64748b;
  display: flex;
  font-size: 0.8rem;
  font-weight: 600;
  justify-content: center;
  width: 100%;
`;

const isProtectedReferenceUrl = (url) => /\/references\/\d+\/content\/?/.test(url || '');

/**
 * Loads reference images for buyers, artists, and shop sellers.
 * Public CDN URLs render directly; auth-gated content endpoints are fetched
 * with credentials and shown via an object URL.
 */
const AuthenticatedReferenceImage = ({
  url,
  reference,
  alt = 'Reference image',
  aspect = '1',
  radius = '0',
  className,
  style,
}) => {
  const resolvedUrl = url || reference?.url;
  const [src, setSrc] = useState(() => (
    resolvedUrl && !isProtectedReferenceUrl(resolvedUrl) ? resolvedUrl : null
  ));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl;
    setFailed(false);

    if (!resolvedUrl) {
      setSrc(null);
      return undefined;
    }

    if (!isProtectedReferenceUrl(resolvedUrl)) {
      setSrc(resolvedUrl);
      return undefined;
    }

    setSrc(null);
    (async () => {
      try {
        const response = await fetch(resolvedUrl, {
          credentials: 'include',
          headers: { Accept: 'image/*,*/*' },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setSrc(objectUrl);
        }
      } catch {
        if (!cancelled) {
          setSrc(null);
          setFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [resolvedUrl]);

  if (!resolvedUrl || failed || !src) {
    return (
      <Placeholder $aspect={aspect} $radius={radius} className={className} style={style}>
        {!resolvedUrl || failed ? 'Image unavailable' : ''}
      </Placeholder>
    );
  }

  return (
    <Image
      className={className}
      src={src}
      alt={alt}
      style={style}
      $aspect={aspect}
      $radius={radius}
      onError={() => setFailed(true)}
    />
  );
};

export default AuthenticatedReferenceImage;
